import { useContainer } from "@src/contexts/container-context";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export function useSimulations(options = {}) {
  const { trekService } = useContainer();

  return useQuery({
    queryKey: ["simulations"],
    queryFn: () => trekService.getSimulations(),
    ...options,
  });
}

/**
 * The in-house career catalog, which names every Trek Mobile career's
 * `transfrId`. Cached like the simulation catalog: it changes only when the
 * career data set does, and it is the whole catalog in one response.
 */
export function useCareers(options = {}) {
  const { trekService } = useContainer();

  return useQuery({
    queryKey: ["careers"],
    queryFn: () => trekService.getCareers(),
    ...options,
  });
}

export function useCareerImageUrl(
  { learningExperienceId, transfrId },
  options = {}
) {
  const { trekService } = useContainer();
  const { data: simulations, isFetched: areSimulationsFetched } =
    useSimulations();

  return useQuery({
    queryKey: ["careerImageUrl", learningExperienceId, transfrId],
    queryFn: () =>
      trekService.getCareerImageUrl({
        learningExperienceId,
        simulations,
        transfrId,
      }),
    enabled: areSimulationsFetched,
    ...options,
  });
}

export function useCareerImageUrls(careers = [], getKey, getParams) {
  const { trekService } = useContainer();

  const { data: simulations, isFetched: areSimulationsFetched } =
    useSimulations();

  const queries = useQueries({
    queries: careers.map((career) => {
      const params = getParams(career);
      return {
        queryKey: [
          "careerImageUrl",
          params.learningExperienceId,
          params.transfrId,
        ],
        queryFn: async () =>
          trekService.getCareerImageUrl({
            learningExperienceId: params.learningExperienceId,
            simulations,
            transfrId: params.transfrId,
          }),
      };
    }),
    enabled: areSimulationsFetched,
  });

  // Convert results to a Map
  return useMemo(() => {
    const urlMap = new Map();
    for (const [index, query] of queries.entries()) {
      const key = getKey(careers[index]);
      if (query.data) {
        urlMap.set(key, query.data);
      }
    }
    return urlMap;
  }, [queries, careers, getKey]);
}
